import { ModelDropdown } from "@/components/molecules/ModelDropdown/ModelDropdown";
import { MultiSelectDropdown } from "@/components/molecules/MultiSelectDropdown/MultiSelectDropdown";
import { lightningLayerConfig } from "@/constants/layerConfigAttributes";
import { FeatureHubGuard } from "@/featureFlags";
import {
  DefaultLightningRenderMode,
  type LightningRenderMode,
  type LightningTypeFilterValue,
  isLightningRenderMode,
  lightningTypeFilterValues,
} from "@/geojson/definitions";
import { batchGroupBy } from "@/reducer/batchGroupBy";
import { t } from "@lingui/core/macro";
import type { ModelSchema } from "@mm/api-layers.meteomatics.com";
import type { LightningLayer } from "@mm/metx-workbench.meteomatics.com";
import React from "react";
import { LightningParamSelector } from "../CommonParts/ParamSelector/LightningParamSelector/LightningParamSelector";
import { OpacityInput } from "../LayerStackWindow/LayerFormElements";
import { FormField, InputFormField } from "../form-field";

type updateLayerProps = {
  onChange: (propsValue: Partial<LightningLayer>, isGrouped?: boolean) => void;
  layer: LightningLayer;
  models: ModelSchema[];
};
const lightningTypeOptionValues: readonly string[] = lightningTypeFilterValues;

const resolveLightningRenderMode = (layer: LightningLayer): LightningRenderMode => {
  const mode = layer.custom_options?.clustering_type;
  if (isLightningRenderMode(mode)) {
    return mode;
  }

  // Keep legacy profiles compatible by falling back to the default mode.
  return DefaultLightningRenderMode;
};

/**
 * Reads persisted lightning type filter values from custom options and keeps only supported values.
 * Invalid/legacy values are ignored to keep UI state stable.
 */
const readLightningTypeFilter = (layer: LightningLayer): LightningTypeFilterValue[] => {
  const customOptions = layer.custom_options;
  const raw = customOptions?.lightning_type_filter;
  if (!Array.isArray(raw) || raw.length === 0) {
    return [];
  }

  const isLightningTypeFilterValue = (value: string): value is LightningTypeFilterValue =>
    lightningTypeOptionValues.includes(value);
  return [...new Set(raw.filter(isLightningTypeFilterValue))];
};

/**
 * Normalizes the UI multiselect payload for persistence.
 * `null` means "no filter" (show all types), which we also use when all values are selected.
 */
const normalizeLightningTypeFilter = (selected: (string | number)[]): string[] | null => {
  const selectedValues = [
    ...new Set(selected.map((value) => String(value)).filter((value) => lightningTypeOptionValues.includes(value))),
  ];
  if (selectedValues.length === 0 || selectedValues.length === lightningTypeFilterValues.length) {
    return null;
  }
  return selectedValues;
};

export const LightningLayerUpdateForm = (props: updateLayerProps) => {
  const clusteringMode = resolveLightningRenderMode(props.layer);
  const activeLightningTypeFilter = readLightningTypeFilter(props.layer);

  return (
    <>
      <FormField label={t`Source`}>
        <ModelDropdown
          options={props.models?.map((model: any) => ({ id: model.identifier, title: `${model.name}` })) || []}
          value={"mix"}
          onChange={(newModel) => {
            props.onChange({ model: newModel });
          }}
          placeholder={"Select a model"}
        />
      </FormField>
      <OpacityInput
        layer={props.layer}
        change={props.onChange}
        onStart={() => batchGroupBy.start()}
        onEnd={() => batchGroupBy.end()}
      />
      <FormField label={t`Interval`}>
        <LightningParamSelector
          onSelect={(parameter) => {
            props.onChange({ parameter_unit: parameter });
          }}
          selectedItemKey={props.layer.parameter_unit}
        />
      </FormField>
      <InputFormField
        label={t`Size`}
        type="range"
        onChange={(event) => props.onChange({ text_size: Number(event.target.value) })}
        {...lightningLayerConfig.elementSizeRange}
        value={Number(props.layer.text_size)}
        onMouseDown={() => batchGroupBy.start()}
        onMouseUp={() => batchGroupBy.end()}
      />
      <FormField label={t`Rendering`}>
        <select
          value={clusteringMode}
          onChange={(event) => {
            const nextMode = isLightningRenderMode(event.target.value)
              ? event.target.value
              : DefaultLightningRenderMode;
            props.onChange({
              custom_options: {
                ...(props.layer.custom_options ?? {}),
                clustering_type: nextMode,
              },
            });
          }}
        >
          <option value="none">{t`None (Icons only)`}</option>
          <option value="heatmap">{"Heatmap"}</option>
          <option value="cluster">{"Cluster"}</option>
        </select>
      </FormField>
      <FormField label={t`Type`}>
        <MultiSelectDropdown
          items={[
            { id: "cloud_to_cloud", label: t`Cloud-to-Cloud` },
            { id: "cloud_to_ground", label: t`Cloud-to-Ground` },
            { id: "unknown", label: t`Unclassified` },
          ]}
          selectedIds={activeLightningTypeFilter}
          onChange={(selected) => {
            const nextFilter = normalizeLightningTypeFilter(selected);
            props.onChange({
              custom_options: {
                ...(props.layer.custom_options ?? {}),
                lightning_type_filter: nextFilter,
              } as LightningLayer["custom_options"],
            });
          }}
          placeholder={t`No filter applied`}
        />
      </FormField>

      <FeatureHubGuard flag="lightning_v2">
        <FormField label={"Lightning V2"}>
          <input
            type="checkbox"
            checked={!!props.layer.custom_options?.lightning_v2}
            onChange={() =>
              props.onChange({
                custom_options: {
                  ...(props.layer.custom_options ?? {}),
                  lightning_v2: !props.layer.custom_options?.lightning_v2,
                },
              })
            }
          />
        </FormField>
      </FeatureHubGuard>
    </>
  );
};
